"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getOrderLineStatusLabel = void 0;
const dayjs = require("dayjs");
const helpers_1 = require("../../helpers");
const getOrderLineStatusLabel = (item) => {
    switch (item.status) {
        case helpers_1.OrderLineStatus.DRAFT:
            return {
                label: "Draft",
                className: "text-grey4 font-semibold",
                bgClassName: "",
            };
        case helpers_1.OrderLineStatus.PENDING_CLIENT_APPROVAL:
            return {
                label: "Pending client approval",
                className: "text-blue font-semibold",
                bgClassName: "",
            };
        case helpers_1.OrderLineStatus.PENDING:
            return {
                label: "Pending",
                className: "text-blue font-semibold",
                bgClassName: "",
            };
        case helpers_1.OrderLineStatus.URGENT_ACTION_REQUIRED:
            return {
                label: "Urgent action required",
                className: "text-orange font-semibold",
                bgClassName: "bg-orange/5",
            };
        case helpers_1.OrderLineStatus.WAITING_TO_SEND:
            return {
                label: "Waiting to send",
                className: "text-orange font-semibold",
                bgClassName: "bg-orange/5",
            };
        case helpers_1.OrderLineStatus.WAITING_FOR_CLIENT_TRANSFER_INFO:
            return {
                label: "Waiting for client transfer info",
                className: "text-orange font-semibold",
                bgClassName: "bg-orange/5",
            };
        case helpers_1.OrderLineStatus.MISSING_SUPPLIER_EMAIL:
            return {
                label: "Missing supplier email",
                className: "text-red font-semibold",
                bgClassName: "bg-red/5",
            };
        case helpers_1.OrderLineStatus.WAITING_FOR_TEE_SHEET: {
            // this is only a status for a golf order line
            const golf_config = item.data;
            const course_policies = golf_config?.course?.policies;
            const advance_booking = course_policies?.advance_booking;
            const weekend_days = item.region.weekend_days;
            // Get the date of play
            const date_of_play = golf_config.where_when_who.date;
            const play_date = new Date(date_of_play);
            play_date.setHours(0, 0, 0, 0); // Reset time to start of day
            // Determine if the date is a weekend or weekday
            const day = play_date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
            const days = {
                sun: day === 0,
                mon: day === 1,
                tue: day === 2,
                wed: day === 3,
                thu: day === 4,
                fri: day === 5,
                sat: day === 6,
            };
            // Check if the play date is on a weekend based on the region's weekend_days configuration
            const isWeekend = Object.entries(weekend_days).some(([dayKey, isWeekendDay]) => {
                return isWeekendDay && days[dayKey];
            });
            // Calculate the applicable booking limit based on whether it's a weekend or weekday
            const applicable_limit = isWeekend
                ? advance_booking?.weekend_ph
                : advance_booking?.weekday;
            if (!applicable_limit) {
                return {
                    label: "Waiting for tee sheet",
                    className: "text-orange font-semibold",
                    bgClassName: "bg-orange/5",
                };
            }
            // Calculate the date when the tee sheet will become available
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time to start of day
            const days_before_play = applicable_limit;
            const tee_sheet_available_date = new Date(play_date);
            tee_sheet_available_date.setDate(tee_sheet_available_date.getDate() - days_before_play);
            // Calculate days until tee sheet becomes available
            let days_until_available = 0;
            if (tee_sheet_available_date > today) {
                // Tee sheet is not yet available
                const time_difference = tee_sheet_available_date.getTime() - today.getTime();
                days_until_available = Math.ceil(time_difference / (1000 * 3600 * 24));
            }
            let label = "Waiting for tee sheet";
            if (days_until_available > 0) {
                label = `Waiting for tee sheet (available in ${days_until_available} day${days_until_available !== 1 ? "s" : ""})`;
            }
            else {
                label = "Tee sheet available soon";
            }
            return {
                label: label,
                className: "text-orange font-semibold",
                bgClassName: "bg-orange/5",
            };
        }
        case helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER:
            return {
                label: "Waiting for supplier",
                className: "text-orange font-semibold",
                bgClassName: "bg-orange/5",
            };
        case helpers_1.OrderLineStatus.WAITING_FOR_SUPPLIER_OVERDUE:
            return {
                label: "Waiting for supplier overdue",
                className: "text-red font-semibold",
                bgClassName: "bg-red/5",
            };
        case helpers_1.OrderLineStatus.CONFIRMED_PAID:
        case helpers_1.OrderLineStatus.CONFIRMED:
            switch (item.type) {
                case "GOLF": {
                    const formatTeeTimeString = (teeTimeGroups) => {
                        let result = `Tee off at `;
                        // Format the first 3 tee time groups
                        const visibleGroups = teeTimeGroups.slice(0, 3);
                        // Add formatted time for each visible group
                        const timeStrings = visibleGroups.map((group) => {
                            if (!group.start_time)
                                return "";
                            // Handle time-only string like "08:10"
                            const [hours, minutes] = group.start_time.split(":");
                            const time = dayjs()
                                .hour(parseInt(hours))
                                .minute(parseInt(minutes));
                            return time.format("h:mm A");
                        });
                        result += timeStrings.join(" / ");
                        // Add "+ X more" if there are more than 3 groups
                        if (teeTimeGroups.length > 3) {
                            result += ` + ${teeTimeGroups.length - 3} more`;
                        }
                        // Calculate total players
                        const totalPlayers = teeTimeGroups.reduce((total, group) => total + group.number_of_players, 0);
                        // Add player count
                        result += ` (${totalPlayers} pax)`;
                        return result;
                    };
                    const golf_config = item.data;
                    return {
                        label: formatTeeTimeString(golf_config.tee_time.groups),
                        className: "text-black/80 uppercase font-semibold",
                        bgClassName: "bg-white",
                        icon: "check",
                        iconColor: "text-green",
                    };
                }
                case "TRANSFER": {
                    const transfer_config = item.data;
                    return {
                        label: `Pick-up at ${dayjs(transfer_config.driver.pickup_time).format("h:mm a")} (${transfer_config.where_when_who.number_of_passengers} pax)`,
                        className: "text-black/80 uppercase font-semibold",
                        bgClassName: "bg-white",
                        icon: "check",
                        iconColor: "text-green",
                    };
                }
                default:
                    return {
                        label: "Confirmed",
                        className: "text-green font-semibold",
                        bgClassName: "bg-green/5",
                        icon: "check",
                        iconColor: "text-grey4",
                    };
            }
        case helpers_1.OrderLineStatus.RATE_REQUESTED:
            return {
                label: "Rate requested",
                className: "text-orange font-semibold",
                bgClassName: "bg-orange/5",
            };
        case helpers_1.OrderLineStatus.CHANGE_REQUESTED:
            return {
                label: "Change requested",
                className: "text-red font-semibold",
                bgClassName: "bg-red/5",
            };
        case helpers_1.OrderLineStatus.UPDATE_PENDING:
            return {
                label: "Update pending",
                className: "text-orange font-semibold",
                bgClassName: "bg-orange/5",
            };
        case helpers_1.OrderLineStatus.ALTERNATIVE_OFFERED:
            return {
                label: "Alternative offered",
                className: "text-blue font-semibold",
                bgClassName: "",
            };
        case helpers_1.OrderLineStatus.ALTERNATIVE_OFFERED_OVERDUE:
            return {
                label: "Alternative offered overdue",
                className: "text-red font-semibold",
                bgClassName: "bg-red/5",
            };
        case helpers_1.OrderLineStatus.CANCELLED:
            return {
                label: "Cancelled",
                className: "text-black90 font-semibold",
                bgClassName: "",
            };
        case helpers_1.OrderLineStatus.CANCELLED_UNAVAILABLE:
            return {
                label: "Cancelled - unavailable",
                className: "text-black90 font-semibold",
                bgClassName: "",
            };
        case helpers_1.OrderLineStatus.CANCELLED_REJECTED_BY_CLIENT:
            return {
                label: "Cancelled - rejected",
                className: "text-black90 font-semibold",
                bgClassName: "",
            };
        default:
            return {
                label: item.status,
                className: "text-black90 font-semibold",
                bgClassName: "",
            };
    }
};
exports.getOrderLineStatusLabel = getOrderLineStatusLabel;
